import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { adminApi } from '../utils/api'
import { t } from '../utils/translations'

const CHURCHES = ['KTC', 'SSC', 'WIC', 'STC']
const POST_UNTIL_DEFAULT_DAYS = 100

function getHongKongTodayStr() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Hong_Kong' }).replace(/-/g, '')
}

function formatDateDisplay(dateStr) {
  if (!dateStr || dateStr.length !== 8) return dateStr
  return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`
}

function getDayInfo(dateStr, lang) {
  if (!dateStr || dateStr.length !== 8) return { text: '', isSunday: false, isSaturday: false }
  const year = parseInt(dateStr.substring(0, 4), 10)
  const month = parseInt(dateStr.substring(4, 6), 10) - 1
  const day = parseInt(dateStr.substring(6, 8), 10)
  const date = new Date(year, month, day)
  const dayNum = date.getDay()
  const daysZh = ['日', '一', '二', '三', '四', '五', '六']
  const daysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return {
    text: lang === 'zh' ? daysZh[dayNum] : daysEn[dayNum],
    isSunday: dayNum === 0,
    isSaturday: dayNum === 6
  }
}

function addDaysYYYYMMDD(dateStr, days) {
  const year = parseInt(dateStr.substring(0, 4), 10)
  const month = parseInt(dateStr.substring(4, 6), 10) - 1
  const day = parseInt(dateStr.substring(6, 8), 10)
  const date = new Date(year, month, day)
  date.setDate(date.getDate() + days)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}${m}${d}`
}

/** Effective post-until: stored value, or event_date + 100 days */
function getEffectivePostUntil(entry) {
  if (entry.post_until && /^\d{8}$/.test(entry.post_until)) return entry.post_until
  if (entry.event_date && /^\d{8}$/.test(entry.event_date)) {
    return addDaysYYYYMMDD(entry.event_date, POST_UNTIL_DEFAULT_DAYS)
  }
  return entry.event_date || ''
}

/**
 * 活動總覽 — chronological upcoming Events (not Sunday 4-church grid).
 * Events can fall on any weekday; group by date, soonest first.
 */
export default function EventsOverviewPage({ lang = 'zh' }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState('upcoming') // upcoming | past
  const [churchFilter, setChurchFilter] = useState('all')
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getOverview('all')
      setOverview(response.data.data)
    } catch (err) {
      console.error(err)
      setError(lang === 'zh' ? '載入失敗' : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const allEvents = useMemo(() => {
    if (!overview?.dates) return []
    const rows = []
    for (const dateGroup of overview.dates) {
      for (const church of CHURCHES) {
        const entries = dateGroup.churches?.[church]?.entries || []
        for (const entry of entries) {
          if (entry.type !== 'Event') continue
          rows.push({
            ...entry,
            church,
            event_date: dateGroup.date
          })
        }
      }
    }
    return rows
  }, [overview])

  const filteredEvents = useMemo(() => {
    const today = getHongKongTodayStr()
    const pastCutoff = addDaysYYYYMMDD(today, -30)

    let list = allEvents.filter(e => {
      if (churchFilter !== 'all' && e.church !== churchFilter) return false
      const postUntil = getEffectivePostUntil(e)
      // Active / upcoming: only while 刊登至 is today or later (ignore event_date once posting ended)
      if (viewMode === 'upcoming') {
        return postUntil >= today
      }
      // Past: posting window ended, but event was within last ~30 days of ending (or event date)
      return postUntil < today && (postUntil >= pastCutoff || e.event_date >= pastCutoff)
    })

    list = list.sort((a, b) => {
      const dateCmp = viewMode === 'upcoming'
        ? a.event_date.localeCompare(b.event_date)
        : b.event_date.localeCompare(a.event_date)
      if (dateCmp !== 0) return dateCmp
      return (a.time || '').localeCompare(b.time || '')
    })

    return list
  }, [allEvents, viewMode, churchFilter])

  const pastEventCount = useMemo(() => {
    const today = getHongKongTodayStr()
    const pastCutoff = addDaysYYYYMMDD(today, -30)
    return allEvents.filter(e => {
      if (churchFilter !== 'all' && e.church !== churchFilter) return false
      const postUntil = getEffectivePostUntil(e)
      return postUntil < today && (postUntil >= pastCutoff || e.event_date >= pastCutoff)
    }).length
  }, [allEvents, churchFilter])

  const groupedByDate = useMemo(() => {
    const groups = []
    let current = null
    for (const entry of filteredEvents) {
      if (!current || current.date !== entry.event_date) {
        current = { date: entry.event_date, entries: [] }
        groups.push(current)
      }
      current.entries.push(entry)
    }
    return groups
  }, [filteredEvents])

  const canEdit = (church) => {
    if (!user || user.role === 'viewer') return false
    if (user.role === 'admin' || user.role === 'sysadmin') return true
    const churches = user.churches || (user.church ? [user.church] : [])
    return churches.includes(church)
  }

  const openEdit = (entry) => {
    if (!canEdit(entry.church)) return
    const params = new URLSearchParams({
      church: entry.church,
      type: 'Event',
      date: entry.event_date,
      from: 'events-overview'
    })
    navigate(`/event?${params.toString()}`)
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <span className="badge badge-success">{t('submitted', lang)}</span>
      case 'saved':
        return <span className="badge badge-warning">{t('saved', lang)}</span>
      default:
        return <span className="badge badge-danger">{status}</span>
    }
  }

  if (loading) {
    return <div className="container">{t('loading', lang)}</div>
  }

  if (error) {
    return <div className="message message-error">{error}</div>
  }

  return (
    <div className="events-overview-page">
      <div className="overview-toolbar">
        <div className="view-toggle" role="group" aria-label={lang === 'zh' ? '檢視範圍' : 'View range'}>
          <button
            type="button"
            className={viewMode === 'upcoming' ? 'active' : ''}
            onClick={() => setViewMode('upcoming')}
          >
            {t('upcomingEvents', lang)}
          </button>
          <button
            type="button"
            className={viewMode === 'past' ? 'active' : ''}
            onClick={() => setViewMode('past')}
          >
            {t('pastEvents', lang)}
          </button>
        </div>

        <div className="church-filter" role="group" aria-label={t('church', lang)}>
          <button
            type="button"
            className={churchFilter === 'all' ? 'active' : ''}
            onClick={() => setChurchFilter('all')}
          >
            {lang === 'zh' ? '全部堂會' : 'All churches'}
          </button>
          {CHURCHES.map(church => (
            <button
              key={church}
              type="button"
              className={churchFilter === church ? 'active' : ''}
              onClick={() => setChurchFilter(church)}
            >
              {t(church, lang)}
            </button>
          ))}
        </div>
      </div>

      {groupedByDate.length === 0 ? (
        <div className="no-events">
          <p>{viewMode === 'upcoming' ? t('noUpcomingEvents', lang) : (lang === 'zh' ? '沒有已結束的活動' : 'No ended events')}</p>
          {viewMode === 'upcoming' && pastEventCount > 0 && (
            <p className="empty-hint">
              {lang === 'zh'
                ? `另有 ${pastEventCount} 項已結束活動 — 可點「已結束（30天內）」查看`
                : `${pastEventCount} ended event(s) — switch to “Ended” to view`}
            </p>
          )}
        </div>
      ) : (
        groupedByDate.map(group => {
          const dayInfo = getDayInfo(group.date, lang)
          const isPast = group.date < getHongKongTodayStr()
          return (
            <section key={group.date} className={`event-date-group ${isPast ? 'past' : ''}`}>
              <header className="event-date-header">
                <h2>
                  {formatDateDisplay(group.date)}
                  <span className={`day-of-week ${dayInfo.isSunday ? 'sunday' : dayInfo.isSaturday ? 'saturday' : ''}`}>
                    ({dayInfo.text})
                  </span>
                </h2>
                <span className="event-count">
                  {group.entries.length} {lang === 'zh' ? '項' : 'event(s)'}
                </span>
              </header>

              <div className="event-cards">
                {group.entries.map(entry => (
                  <article
                    key={entry.entry_id || `${entry.church}-${entry.title}-${entry.time}`}
                    className="event-card"
                    onClick={() => setSelected(entry)}
                  >
                    <div className="event-card-main">
                      <div className="event-card-meta">
                        <span className="event-church">{t(entry.church, lang)}</span>
                        <span className="event-time">{entry.time || '—'}</span>
                      </div>
                      <h3 className="event-title" title={entry.title}>{entry.title}</h3>
                      {entry.speaker && (
                        <p className="event-speaker">{entry.speaker}</p>
                      )}
                      {(entry.post_until || entry.poster_key || entry.video_url || entry.event_date < getHongKongTodayStr()) && (
                        <p className="event-extra">
                          {entry.event_date < getHongKongTodayStr() && (
                            <span className="tag muted">{lang === 'zh' ? '活動已過' : 'Event passed'}</span>
                          )}
                          {getEffectivePostUntil(entry) < getHongKongTodayStr() && (
                            <span className="tag muted">{lang === 'zh' ? '刊登已結束' : 'Posting ended'}</span>
                          )}
                          <span>
                            {lang === 'zh' ? '刊登至' : 'Until'}{' '}
                            {formatDateDisplay(getEffectivePostUntil(entry))}
                          </span>
                          {entry.poster_key && <span className="tag">Poster</span>}
                          {entry.video_url && <span className="tag">Video</span>}
                          {entry.post_to_main === false && (
                            <span className="tag muted">{lang === 'zh' ? '不上主頁' : 'Not on main'}</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="event-card-side">
                      {getStatusBadge(entry.status)}
                      {(entry.post_to_main === false || getEffectivePostUntil(entry) < getHongKongTodayStr()) && (
                        <span className="badge badge-not-shown">
                          {lang === 'zh' ? '不顯示' : 'Not shown'}
                        </span>
                      )}
                      {entry.file_urls?.length > 0 && (
                        <span className="file-indicator" title={lang === 'zh' ? '附件' : 'Files'}>
                          📎{entry.file_urls.length}
                        </span>
                      )}
                      {canEdit(entry.church) && (
                        <button
                          type="button"
                          className="btn-edit-event"
                          onClick={(e) => {
                            e.stopPropagation()
                            openEdit(entry)
                          }}
                        >
                          {t('edit', lang)}
                        </button>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )
        })
      )}

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selected.title}</h3>
              <button type="button" className="btn-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="entry-row">
                <span className="label">{t('eventDate', lang)}:</span>
                <span>{formatDateDisplay(selected.event_date)}</span>
              </div>
              <div className="entry-row">
                <span className="label">{t('church', lang)}:</span>
                <span>{t(selected.church, lang)}</span>
              </div>
              {selected.time && (
                <div className="entry-row">
                  <span className="label">{t('time', lang)}:</span>
                  <span>{selected.time}</span>
                </div>
              )}
              {selected.speaker && (
                <div className="entry-row">
                  <span className="label">{t('speaker', lang)}:</span>
                  <span>{selected.speaker}</span>
                </div>
              )}
              {selected.description && (
                <div className="entry-row">
                  <span className="label">{t('description', lang)}:</span>
                  <span>{selected.description}</span>
                </div>
              )}
              <div className="entry-row">
                <span className="label">{t('status', lang)}:</span>
                {getStatusBadge(selected.status)}
              </div>
              {selected.file_urls?.length > 0 && (
                <div className="entry-row">
                  <span className="label">{lang === 'zh' ? '附件' : 'Files'}:</span>
                  <span>{selected.file_urls.length}</span>
                </div>
              )}
            </div>
            {canEdit(selected.church) && (
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setSelected(null)}>
                  {t('cancel', lang)}
                </button>
                <button type="button" className="btn-primary" onClick={() => openEdit(selected)}>
                  {t('edit', lang)}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .events-overview-page {
          min-height: 60vh;
          width: 100%;
        }

        .overview-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px 20px;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
        }

        .view-toggle,
        .church-filter {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .view-toggle button,
        .church-filter button {
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          background: var(--bg-card);
          color: var(--text-secondary, #a0aec0);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .view-toggle button.active,
        .church-filter button.active {
          background: var(--accent-primary);
          border-color: transparent;
          color: #fff;
        }

        .no-events {
          padding: 48px 16px;
          text-align: center;
          color: var(--text-secondary, #a0aec0);
        }

        .no-events .empty-hint {
          margin-top: 12px;
          font-size: 0.85rem;
          color: var(--text-secondary, #a0aec0);
        }

        .event-date-group {
          margin-bottom: 28px;
        }

        .event-date-group.past {
          opacity: 0.85;
        }

        .event-date-header {
          display: flex;
          align-items: baseline;
          gap: 12px;
          margin-bottom: 12px;
        }

        .event-date-header h2 {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 600;
        }

        .day-of-week {
          margin-left: 8px;
          font-weight: 400;
          color: var(--text-secondary, #a0aec0);
          font-size: 0.95rem;
        }

        .day-of-week.sunday {
          color: #f56565;
        }

        .day-of-week.saturday {
          color: #63b3ed;
        }

        .event-count {
          font-size: 0.8rem;
          color: var(--text-secondary, #a0aec0);
        }

        .event-cards {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .event-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 14px 16px;
          background: var(--bg-card);
          border-radius: var(--border-radius, 8px);
          border-left: 4px solid var(--accent-primary, #c12b20);
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
        }

        .event-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
        }

        .event-card-main {
          min-width: 0;
          flex: 1;
        }

        .event-card-meta {
          display: flex;
          gap: 12px;
          align-items: center;
          margin-bottom: 4px;
          font-size: 0.8rem;
          color: var(--text-secondary, #a0aec0);
        }

        .event-church {
          font-weight: 600;
          color: var(--text-primary, #e2e8f0);
        }

        .event-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .event-speaker {
          margin: 4px 0 0;
          font-size: 0.85rem;
          color: var(--text-secondary, #a0aec0);
        }

        .event-extra {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin: 6px 0 0;
          font-size: 0.75rem;
          color: var(--text-secondary, #a0aec0);
        }

        .event-extra .tag {
          padding: 1px 6px;
          border-radius: 4px;
          background: rgba(193, 43, 32, 0.12);
          color: var(--accent-primary);
          font-weight: 600;
        }

        .event-extra .tag.muted {
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .event-card-side {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .badge-not-shown {
          background: #fde8e8;
          color: #b91c1c;
          border: 1px solid #f5c2c2;
          font-size: 0.75rem;
          padding: 2px 8px;
          border-radius: 999px;
          font-weight: 600;
        }

        .file-indicator {
          font-size: 0.85rem;
        }

        .btn-edit-event {
          padding: 4px 10px;
          border-radius: 4px;
          border: none;
          background: var(--accent-primary);
          color: #fff;
          font-size: 0.8rem;
          cursor: pointer;
        }

        .btn-edit-event:hover {
          filter: brightness(1.1);
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }

        .modal {
          background: var(--bg-card, #1a202c);
          border-radius: 8px;
          max-width: 480px;
          width: 100%;
          max-height: 80vh;
          overflow: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
        }

        .btn-close {
          background: none;
          border: none;
          color: inherit;
          font-size: 1.5rem;
          cursor: pointer;
          line-height: 1;
        }

        .modal-body {
          padding: 16px 20px;
        }

        .entry-row {
          display: flex;
          gap: 8px;
          margin-bottom: 10px;
          font-size: 0.9rem;
        }

        .entry-row .label {
          flex-shrink: 0;
          min-width: 72px;
          color: var(--text-secondary, #a0aec0);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 12px 20px 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .btn-secondary,
        .btn-primary {
          padding: 8px 16px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          color: inherit;
        }

        .btn-primary {
          background: var(--accent-primary);
          color: #fff;
        }

        @media (max-width: 640px) {
          .event-card {
            flex-direction: column;
            align-items: flex-start;
          }

          .event-card-side {
            width: 100%;
            justify-content: flex-start;
          }
        }
      `}</style>
    </div>
  )
}
